import DebounceInput from '@/components/UI/DebounceInput';
import { hasOwnProperty } from '@/helpers';
import { _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';
import cn from 'classnames';
import type { FC, FocusEvent } from 'react';
import './IndividualSaveRepeater.scss';
import type { Errors } from './IndividualSaveRepeater.utils';
import { METADATA_TITLE_MAX_LENGTH } from './IndividualSaveRepeater.constants';

export interface IndividualMetadataTitleProps {
  errors: Errors[];
  title?: string;
  id?: ID;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  hasDuplicate?: boolean;
}

export const IndividualMetadataTitle: FC<IndividualMetadataTitleProps> = ({
  title,
  errors,
  id,
  handleChange,
  onBlur,
  disabled,
  hasDuplicate,
}): JSX.Element => {
  const hasErrors = errors.find(({ id: fieldId }) => String(fieldId) === String(id));
  const hasValidationErrors = _notNil(hasErrors) && hasOwnProperty(hasErrors.errors, 'title');
  const hasBlankError =
    hasValidationErrors && hasErrors.errors.title?.some((e: string) => e.includes("can't be blank"));
  const trimmedTitle = title?.trim() ?? '';
  const hasActualContent = trimmedTitle.length > 0;
  const shouldShowValidationError = hasValidationErrors && (hasBlankError === false || hasActualContent === false);
  const isTooLong = trimmedTitle.length > METADATA_TITLE_MAX_LENGTH;

  const tooltipContent = isTooLong
    ? `Field name can't exceed ${METADATA_TITLE_MAX_LENGTH} characters`
    : hasDuplicate === true
      ? 'Duplicate titles are not allowed'
      : shouldShowValidationError === true
        ? (hasErrors?.errors?.title?.join('; ') ?? '')
        : '';

  return (
    <DebounceInput
      handleChange={handleChange}
      inputProps={{
        name: 'title',
        value: title,
        'data-id': id,
        type: 'text',
        className: cn('z-5 relative input-title-glossary', {
          input__error: shouldShowValidationError === true || hasDuplicate === true || isTooLong,
        }),
        'data-tooltip-id': 'global-tooltip-id',
        'data-tooltip-content': tooltipContent,
        disabled,
        onBlur,
      }}
    />
  );
};
